import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useStore } from '../lib/store';
import { subscribeToPush } from '../lib/push';

function initials(name: string): string {
  return name.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

const TONES = ['clay', 'water', 'sage', 'plum', 'dune'] as const;
type Tone = typeof TONES[number];
function toneFor(id: string): Tone {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return TONES[Math.abs(h) % TONES.length];
}

function GearIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function timeOfDay(h: number): { period: string; slot: 'morning' | 'afternoon' | 'evening' | 'night' } {
  if (h >= 5 && h < 12) return { period: 'Morning', slot: 'morning' };
  if (h >= 12 && h < 17) return { period: 'Afternoon', slot: 'afternoon' };
  if (h >= 17 && h < 22) return { period: 'Evening', slot: 'evening' };
  return { period: 'Night', slot: 'night' };
}

export function Contacts() {
  const nav = useNavigate();
  const user = useStore((s) => s.user);
  const cove = useStore((s) => s.cove);
  const contacts = useStore((s) => s.contacts);
  const messages = useStore((s) => s.messages);
  const setContacts = useStore((s) => s.setContacts);
  const [pushOn, setPushOn] = useState<boolean | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [tod] = useState(() => timeOfDay(new Date().getHours()));

  useEffect(() => {
    api.contacts().then(setContacts).catch(() => {});
  }, [setContacts]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready.then((reg) =>
      reg.pushManager.getSubscription().then((sub) => setPushOn(!!sub)),
    );
  }, []);

  async function enablePush() {
    const ok = await subscribeToPush();
    setPushOn(ok);
    if (!ok) alert("Notifications were not enabled. On iPhone, first tap Share → Add to Home Screen, open the installed app, then try again.");
  }

  const threads = useMemo(() => {
    if (!user) return [];
    const rows = contacts.map((c) => {
      const tid = [user.id, c.id].sort().join(':');
      const msgs = messages[tid] || [];
      const last = msgs[msgs.length - 1];
      return { contact: c, last };
    });
    // Sort: by last message time desc; contacts without messages at the bottom in original order.
    rows.sort((a, b) => (b.last?.createdAt ?? 0) - (a.last?.createdAt ?? 0));
    return rows;
  }, [contacts, messages, user]);

  const onlineCount = contacts.filter((c) => c.online).length;
  const presenceLine =
    contacts.length === 0
      ? 'Invite someone to fill your cove'
      : onlineCount === 0
        ? 'Quiet for now'
        : onlineCount === 1
          ? '1 here now'
          : `${onlineCount} here now`;

  return (
    <div className="app">
      <header className={`cove-hero time-${tod.slot}`}>
        <div className="cove-hero-body">
          <div className="label">{(cove?.name ?? 'Your cove').toUpperCase()}</div>
          <h1>
            {tod.period} <em>at your cove</em>
          </h1>
          <div className="cove-stat">{presenceLine}</div>
        </div>
        <button className="icon-btn-sq" aria-label="Settings" onClick={() => nav('/settings')}>
          <GearIcon />
        </button>
      </header>

      {pushOn === false && (
        <div style={{ padding: '0 var(--s-5)', marginBottom: 'var(--s-4)' }}>
          <div className="banner">
            <span style={{ flex: 1 }}>Turn on notifications so you don't miss calls.</span>
            <button className="btn" style={{ minHeight: 40, padding: '8px 14px', fontSize: 14 }} onClick={enablePush}>Enable</button>
          </div>
        </div>
      )}

      <div className="section-label">Members · {contacts.length}</div>
      <div className="members">
        {contacts.map((c) => (
          <Link key={c.id} className="member" to={`/chat/${c.id}`}>
            <div className={`avatar lg`} data-tone={toneFor(c.id)}>
              {initials(c.displayName)}
              <span className={`dot ${c.online ? 'online' : ''}`} />
            </div>
            <span className="name">{c.displayName}</span>
          </Link>
        ))}
        {contacts.length === 0 && (
          <div style={{ gridColumn: '1 / -1', padding: 'var(--s-5) 0', textAlign: 'center' }} className="muted small">
            No members yet — invite someone to start.
          </div>
        )}
      </div>

      <div className="section-row">
        <div className="section-label" style={{ padding: 0, margin: 0 }}>Threads</div>
        {threads.length > 4 && <a className="link">See all</a>}
      </div>

      <div className="threads">
        {threads.map(({ contact: c, last }) => {
          const isMine = last?.senderId === user?.id;
          const preview = last ? (isMine ? `You: ${last.body}` : last.body) : 'No messages yet — say hello.';
          const time = last ? formatRelative(last.createdAt) : '';
          return (
            <Link key={c.id} className={`thread-row`} to={`/chat/${c.id}`}>
              <div className={`avatar md`} data-tone={toneFor(c.id)}>
                {initials(c.displayName)}
                <span className={`dot sm ${c.online ? 'online' : ''}`} />
              </div>
              <div className="body">
                <div className="row-top">
                  <span className="name">{c.displayName}</span>
                  <span className="time">{time}</span>
                </div>
                <div className="preview">{preview}</div>
              </div>
            </Link>
          );
        })}
      </div>

      <button className="fab" aria-label="Invite someone" onClick={() => setInviteOpen(true)}>
        <PlusIcon />
      </button>

      {inviteOpen && <InviteSheet onClose={() => setInviteOpen(false)} />}
    </div>
  );
}

function InviteSheet({ onClose }: { onClose: () => void }) {
  const [note, setNote] = useState('');
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    try {
      const r = await api.createInvite(note || undefined);
      setUrl(r.url);
    } finally {
      setBusy(false);
    }
  }
  async function copy() { if (url) await navigator.clipboard.writeText(url); }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        {!url ? (
          <>
            <h3 className="display" style={{ margin: 0, fontSize: 'var(--fs-xl)' }}>Invite someone to your cove</h3>
            <p className="muted small" style={{ marginTop: 0 }}>
              They get a one-use link, valid for a few days. Only you can see it.
            </p>
            <label className="field">
              <span className="field-label">Note (optional)</span>
              <input
                className="input"
                placeholder="e.g. Grandma, sitter Kate…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
            <button className="btn block" onClick={create} disabled={busy}>
              {busy ? 'Creating…' : 'Create invite link'}
            </button>
            <button className="btn secondary block" onClick={onClose}>Cancel</button>
          </>
        ) : (
          <>
            <h3 className="display" style={{ margin: 0, fontSize: 'var(--fs-xl)' }}>Share this link</h3>
            <p className="muted small" style={{ marginTop: 0 }}>Valid for a few days · usable once.</p>
            <input
              className="input"
              value={url}
              readOnly
              onFocus={(e) => e.currentTarget.select()}
              style={{ fontSize: 'var(--fs-sm)' }}
            />
            <button className="btn block" onClick={copy}>Copy link</button>
            <button className="btn secondary block" onClick={onClose}>Done</button>
          </>
        )}
      </div>
    </div>
  );
}

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h`;
  const d = new Date(ts);
  const wk = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  if (diff < 6 * 86400_000) return wk;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}
